import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { BellRinging } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

import SearchFiltersForm from "@/pages/grant-search/SearchFiltersForm";
import SearchResultsList from "@/pages/grant-search/SearchResultsList";
import SaveSearchDialog from "@/pages/grant-search/SaveSearchDialog";
import PresetChips, { PRESET_FILTERS, US_STATES } from "@/pages/grant-search/PresetChips";
import OrgProfileAssistant from "@/components/OrgProfileAssistant";

export default function GrantSearch() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [agency, setAgency] = useState("all");
  const [preset, setPreset] = useState("all");
  const [stateCode, setStateCode] = useState("all");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hitCount, setHitCount] = useState(0);
  const [page, setPage] = useState(0);
  const [source, setSource] = useState("federal");
  const [err, setErr] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveFreq, setSaveFreq] = useState("daily");
  const [saveDigestMode, setSaveDigestMode] = useState("summary");
  const [saveSmsEnabled, setSaveSmsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  // Build the search body. Preset filter is layered on TOP of explicit selections:
  // its agency/eligibility codes are merged. For `state` preset, the state name
  // (e.g. "California") is appended to the keyword so Grants.gov surfaces
  // opportunities that mention that state in title/description.
  const buildBody = (kwInput = keyword) => {
    const presetDef = PRESET_FILTERS[preset] || PRESET_FILTERS.all;
    const presetChips = presetDef.chips || {};
    let kw = kwInput;
    if (preset === "state" && stateCode && stateCode !== "all") {
      const stateName = (US_STATES.find(([c]) => c === stateCode) || [])[1];
      kw = stateName ? `${kwInput ? kwInput + " " : ""}${stateName}`.trim() : kwInput;
    }
    const body = { keyword: kw || undefined, rows: 25 };
    if (status !== "all") body.statuses = [status];
    const agencyList = [];
    if (agency !== "all") agencyList.push(agency);
    if (presetChips.agencies) presetChips.agencies.forEach((a) => !agencyList.includes(a) && agencyList.push(a));
    if (agencyList.length) body.agencies = agencyList;
    if (presetChips.eligibilities) body.eligibilities = presetChips.eligibilities;
    return body;
  };

  const PAGE_SIZE = 25;

  const runSearch = async (e, kwOverride, pageOverride = 0, sourceOverride) => {
    if (e) e.preventDefault();
    const src = sourceOverride ?? source;
    setLoading(true);
    setErr("");
    try {
      const body = buildBody(kwOverride ?? keyword);
      body.startRecordNum = pageOverride * PAGE_SIZE;
      const endpoints = { federal: "/grants/search", ca: "/grants/ca/search", il: "/grants/il/search", programs: "/grants/programs/search" };
      const { data } = await api.post(endpoints[src] || "/grants/search", body);
      if (src === "programs") {
        setResults((data.programs || []).map((p) => ({
          id: p.program_id,
          number: `CFDA ${p.cfda}`,
          title: p.title,
          agency: p.department,
          agencyCode: "FED PROGRAM",
          status: "program",
          external_url: p.sam_url || p.website,
        })));
      } else {
        setResults(data.oppHits || []);
      }
      setHitCount(data.hitCount || 0);
      setPage(pageOverride);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const switchSource = (src) => {
    setSource(src);
    if (src === "programs" && !keyword.trim()) {
      setResults([]);
      setHitCount(0);
      setErr("Enter a keyword to search federal programs (CFDA).");
      return;
    }
    runSearch(null, undefined, 0, src);
  };

  const goToPage = (p) => {
    runSearch(null, undefined, p);
    document.querySelector('[data-testid="search-results"]')?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (params.get("q")) runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCurrent = async () => {
    if (!saveName.trim()) { toast.error("Name your saved search"); return; }
    setSaving(true);
    try {
      const built = buildBody();
      const body = { name: saveName, frequency: saveFreq, digest_mode: saveDigestMode, sms_enabled: saveSmsEnabled };
      if (built.keyword) body.keyword = built.keyword;
      if (built.statuses) body.statuses = built.statuses;
      if (built.agencies) body.agencies = built.agencies;
      if (built.eligibilities) body.eligibilities = built.eligibilities;
      await api.post("/saved-searches", body);
      toast.success("Saved — you'll get alerts on new postings.");
      setSaveOpen(false);
      setSaveName("");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Grants.gov — live search</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Find a federal grant.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Real-time search powered by the official Grants.gov public API. Filter by agency,
        status, and keywords to shortlist the opportunities that match your mission.
      </p>

      <div className="mt-6">
        <OrgProfileAssistant
          onKeywordSelect={(kw) => {
            setKeyword(kw);
            runSearch(null, kw);
          }}
        />
      </div>

      <div className="mt-6 flex items-center gap-0 border border-zinc-300 w-fit" data-testid="source-toggle">
        {[
          { key: "federal", label: "Federal · Grants.gov" },
          { key: "ca", label: "California · State" },
          { key: "il", label: "Illinois · State" },
          { key: "programs", label: "Programs · CFDA" },
        ].map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => switchSource(s.key)}
            data-testid={`source-${s.key}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
              source === s.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {source === "federal" && (
        <PresetChips
          preset={preset}
          onPreset={setPreset}
          stateCode={stateCode}
          onStateChange={setStateCode}
        />
      )}

      <SearchFiltersForm
        keyword={keyword} setKeyword={setKeyword}
        status={status} setStatus={setStatus}
        agency={agency} setAgency={setAgency}
        loading={loading} hitCount={hitCount}
        onSubmit={runSearch}
      />

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => { setSaveName(keyword ? `${keyword} alerts` : "My saved search"); setSaveOpen(true); }}
          data-testid="save-search-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline uppercase tracking-wide"
        >
          <BellRinging size={14} /> Save this search & get alerts
        </button>
      </div>

      <SaveSearchDialog
        open={saveOpen} onOpenChange={setSaveOpen}
        saveName={saveName} setSaveName={setSaveName}
        saveFreq={saveFreq} setSaveFreq={setSaveFreq}
        saveDigestMode={saveDigestMode} setSaveDigestMode={setSaveDigestMode}
        saveSmsEnabled={saveSmsEnabled} setSaveSmsEnabled={setSaveSmsEnabled}
        smsAvailable={!!user?.sms_opt_in && !!user?.phone_number}
        filterSummary={`kw: ${keyword || "—"} · status: ${status} · agency: ${agency}`}
        saving={saving}
        onConfirm={saveCurrent}
      />

      {err && (
        <div data-testid="search-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
          {String(err)}
        </div>
      )}

      <SearchResultsList
        loading={loading}
        results={results}
        hitCount={hitCount}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={goToPage}
        onOpen={(g) => {
          if (g.external_url) { window.open(g.external_url, "_blank", "noopener"); return; }
          navigate(String(g.id).startsWith("ca-") ? `/grants/ca/${String(g.id).slice(3)}` : `/grants/${g.id}`);
        }}
      />
    </div>
  );
}
